import {
  BrandKitChangeNoteMaxLength,
  BrandKitCompleteFields,
  BrandKitField,
  BrandKitLimits,
  BrandKitVersionDto,
  diffBrandKitFields,
  isBrandKitDiffEmpty,
} from "@arrowsup/platform-dtos";
import AddIcon from "@mui/icons-material/Add";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { isAxiosError } from "axios";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNotify } from "react-admin";
import * as ss from "superstruct";
import {
  useBrandAssetLibrary,
  useBrandKitDraft,
  useBrandKitDraftMutations,
  useRefreshBrandKit,
} from "../../logic/brand-kit/use-brand-kit";
import { AddFieldDialog } from "./add-field-dialog";
import { BrandAssetsProvider } from "./brand-assets";
import { BrandKitDiffView } from "./brand-kit-diff-view";
import { BrandKitFieldEditor, moveItem } from "./brand-kit-field-editor";
import { versionName } from "./brand-kit-field-value";

const validationMessage = (fields: BrandKitField[]): string | undefined => {
  const [err] = ss.validate(fields, BrandKitCompleteFields);
  if (!err) {
    return undefined;
  }
  return err.refinement === "BrandKitFields" ||
    err.refinement === "BrandKitCompleteFields"
    ? err.message
    : "Some fields have invalid values. Fix the highlighted fields to save.";
};

const draftStatus = (
  draft: BrandKitVersionDto,
  active?: BrandKitVersionDto
): string => {
  if (draft.updatedBy) {
    const at = new Date(draft.updatedAt).toLocaleString();
    return `Last saved by ${draft.updatedBy} on ${at}`;
  }
  if (active) {
    return `Started from ${versionName(active)}`;
  }
  return "Started from the default template";
};

const PublishDialog: React.FC<{
  open: boolean;
  active?: BrandKitVersionDto;
  draft: BrandKitVersionDto;
  publishing: boolean;
  onClose: () => void;
  onPublish: (changeNote: string) => void;
}> = ({ open, active, draft, publishing, onClose, onPublish }) => {
  const [changeNote, setChangeNote] = useState("");
  const diff = useMemo(
    () => diffBrandKitFields(active?.fields ?? [], draft.fields),
    [active, draft]
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        Publish version {(active?.versionNumber ?? 0) + 1}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField
            label="What changed?"
            value={changeNote}
            onChange={(e) => setChangeNote(e.target.value)}
            inputProps={{ maxLength: BrandKitChangeNoteMaxLength }}
            multiline
            minRows={2}
            autoFocus
            fullWidth
          />
          <Typography variant="subtitle2">
            {active ? `Changes since ${versionName(active)}` : "First version"}
          </Typography>
          <BrandAssetsProvider assets={active?.assets ?? []}>
            <BrandKitDiffView diff={diff} />
          </BrandAssetsProvider>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={publishing}
          onClick={() => onPublish(changeNote)}
        >
          {publishing ? "Publishing..." : "Publish"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** Edits the draft and publishes it. Only for users who can update the kit. */
export const BrandKitDraftEditor: React.FC<{
  active?: BrandKitVersionDto;
}> = ({ active }) => {
  const notify = useNotify();
  const { data: draft, isLoading } = useBrandKitDraft();
  // Fields may use assets picked since the draft was saved.
  const { data: library } = useBrandAssetLibrary();
  const assets = useMemo(
    () => [...(draft?.assets ?? []), ...(library ?? [])],
    [draft, library]
  );
  const { save, publish, discard } = useBrandKitDraftMutations();
  const refresh = useRefreshBrandKit();
  const [fields, setFields] = useState<BrandKitField[]>([]);
  const [adding, setAdding] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [conflict, setConflict] = useState(false);
  // Validation errors stay hidden until someone tries to save or publish.
  const [showErrors, setShowErrors] = useState(false);
  // What the in-flight save sent; its response mustn't undo edits made since.
  const submittedRef = useRef<BrandKitField[]>();

  // Every fetch signs new asset links, so only a new draft or revision resets
  // the fields; any refetch would otherwise discard unsaved edits.
  const latestDraft = useRef(draft);
  latestDraft.current = draft;
  const draftId = draft?.id;
  const draftRevision = draft?.revision;
  useEffect(() => {
    const current = latestDraft.current;
    if (!current) {
      return;
    }
    setShowErrors(false);
    const submitted = submittedRef.current;
    submittedRef.current = undefined;
    if (
      submitted &&
      isBrandKitDiffEmpty(diffBrandKitFields(submitted, current.fields))
    ) {
      return;
    }
    setFields(current.fields);
  }, [draftId, draftRevision]);

  const dirty = useMemo(
    () =>
      draft !== undefined &&
      !isBrandKitDiffEmpty(diffBrandKitFields(draft.fields, fields)),
    [draft, fields]
  );
  const unpublished = useMemo(
    () =>
      draft !== undefined &&
      (!active ||
        !isBrandKitDiffEmpty(diffBrandKitFields(active.fields, draft.fields))),
    [active, draft]
  );
  const invalid = useMemo(() => validationMessage(fields), [fields]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }
  if (!draft) {
    return <Alert severity="error">Failed to load the draft</Alert>;
  }

  const ref = { id: draft.id, revision: draft.revision };

  const onError = (e: unknown) => {
    if (isAxiosError(e) && e.response?.status === 409) {
      setConflict(true);
    } else {
      notify("Brand kit update failed", { type: "error" });
    }
  };

  // Someone may have published, so refresh the active kit too.
  const reload = async () => {
    try {
      await refresh();
      setConflict(false);
    } catch {
      notify("Failed to reload the brand kit", { type: "error" });
    }
  };

  const update = (i: number, field: BrandKitField) =>
    setFields((prev) => prev.map((f, j) => (j === i ? field : f)));

  const remove = (i: number) =>
    setFields((prev) => prev.filter((_, j) => j !== i));

  const revert = () => {
    setFields(draft.fields);
    setShowErrors(false);
  };

  const onSave = () => {
    if (invalid) {
      setShowErrors(true);
      return;
    }
    submittedRef.current = fields;
    save.mutate(
      { ...ref, fields },
      {
        onSuccess: () => notify("Draft saved"),
        onError: (e) => {
          submittedRef.current = undefined;
          onError(e);
        },
      }
    );
  };

  const onDiscard = () => {
    const from = active ? versionName(active) : "the default template";
    if (window.confirm(`Discard the draft and start over from ${from}?`)) {
      discard.mutate(ref, { onError });
    }
  };

  const onPublish = (changeNote: string) =>
    publish.mutate(
      { ...ref, changeNote: changeNote.trim() || undefined },
      {
        onSuccess: (version) => {
          setPublishing(false);
          notify(`Published ${versionName(version)}`);
        },
        onError: (e) => {
          setPublishing(false);
          onError(e);
        },
      }
    );

  return (
    <BrandAssetsProvider assets={assets}>
      <Stack spacing={2}>
        {conflict && (
          <Alert
            severity="warning"
            action={
              <Button color="inherit" onClick={() => void reload()}>
                Reload
              </Button>
            }
          >
            Someone else changed or published the draft. Reload to get their
            version; your unsaved changes will be lost.
          </Alert>
        )}

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="body2" color="text.secondary" flex={1}>
            {draftStatus(draft, active)}
            {dirty ? " · Unsaved changes" : ""}
          </Typography>
          <Button disabled={!dirty} onClick={revert}>
            Revert
          </Button>
          <Button
            color="error"
            disabled={discard.isPending || !unpublished}
            onClick={onDiscard}
          >
            Discard draft
          </Button>
          <Button
            variant="outlined"
            disabled={!dirty || save.isPending}
            onClick={onSave}
          >
            {save.isPending ? "Saving..." : "Save draft"}
          </Button>
          <Button
            variant="contained"
            disabled={dirty || !unpublished}
            title={dirty ? "Save your changes first" : undefined}
            onClick={() =>
              invalid ? setShowErrors(true) : setPublishing(true)
            }
          >
            Publish
          </Button>
        </Stack>

        {showErrors && invalid && <Alert severity="error">{invalid}</Alert>}

        {fields.map((field, i) => (
          <BrandKitFieldEditor
            key={field.key}
            field={field}
            showErrors={showErrors}
            onChange={(f) => update(i, f)}
            onRemove={() => remove(i)}
            onMoveUp={
              i > 0
                ? () => setFields((prev) => moveItem(prev, i, i - 1))
                : undefined
            }
            onMoveDown={
              i < fields.length - 1
                ? () => setFields((prev) => moveItem(prev, i, i + 1))
                : undefined
            }
          />
        ))}

        <Box>
          <Button
            startIcon={<AddIcon />}
            disabled={fields.length >= BrandKitLimits.fields}
            onClick={() => setAdding(true)}
          >
            Add field
          </Button>
        </Box>

        <AddFieldDialog
          open={adding}
          existingKeys={fields.map((f) => f.key)}
          onClose={() => setAdding(false)}
          onAdd={(field) => setFields((prev) => [...prev, field])}
        />
        {publishing && (
          <PublishDialog
            open
            active={active}
            draft={draft}
            publishing={publish.isPending}
            onClose={() => setPublishing(false)}
            onPublish={onPublish}
          />
        )}
      </Stack>
    </BrandAssetsProvider>
  );
};
